'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import DynamicReactECharts from './DynamicReactECharts';

import type { TradeData } from '@/lib/types/tradeData';
import { formatCurrency, formatPriceDisplay } from '@/lib/utils/format';
import { formatTime } from '@/lib/utils/day.utils';
import type { CandleSnapshot } from '@/lib/utils/hyper';
import { filterAndMergeTrades } from '@/lib/utils/trade.utils';
import { Box } from '@chakra-ui/react';
import { ChartInterval } from '../trader/PositionDetailChart';
import { MAX_WIDTH_IN_WINDOW } from '@/lib/config/page';

// Use a function to safely get screen width (avoids SSR issues)
const getScreenWidth = () => {
  if (typeof window === 'undefined') return MAX_WIDTH_IN_WINDOW;
  return Math.min(window.innerWidth, MAX_WIDTH_IN_WINDOW);
};

interface InteractiveLineChartProps {
  priceData: CandleSnapshot | undefined;
  tradeData: TradeData[] | undefined;
  interval: ChartInterval;
  width?: number | string;
  height?: number;
  precision?: number;
}

function useChartData(
  priceData: CandleSnapshot | undefined,
  tradeData: TradeData[] | undefined,
) {
  const t = useTranslations();
  return useMemo(() => {
    console.info('Chart data processing:', { priceData, tradeData });
    if (!priceData) return { priceData: [], tradePoints: [] };

    // Filter and merge trades based on price data timestamps
    const mergedTrades =
      tradeData && priceData ? filterAndMergeTrades(tradeData, priceData) : [];

    console.info('Merged trades:', {
      originalCount: tradeData?.length || 0,
      mergedCount: mergedTrades.length,
    });

    const chartPriceData = priceData.map((item) => [item.t, +item.c]);

    // const tradePoints = mergedTrades.map(trade => ({
    //   value: [trade.timestamp, trade.price],
    //   type: trade.type,
    //   amount: trade.amount,
    //   leverage: trade.leverage,
    //   symbolSize: 16,
    //   itemStyle: {
    //     color: trade.type === 'buy' ? '#179B90' : '#E34376'
    //   },
    //   label: {
    //     show: true,
    //     formatter: trade.type === 'buy' ? 'B' : 'S',
    //     position: 'inside',
    //     color: '#fff',
    //     fontSize: 10,
    //     fontWeight: 'bold'
    //   }
    // }))

    // 创建交易标记点数据，参考app端实现
    const tradePoints = mergedTrades
      .map((trade) => {
        // 找到对应的价格数据索引
        const priceIndex = priceData.findIndex(
          (item) => item.t === trade.timestamp,
        );
        if (priceIndex === -1) return null;

        return {
          value: [priceIndex, trade.price], // 使用索引而不是时间戳
          type: trade.type,
          amount: trade.amount,
          leverage: trade.leverage,
          symbolSize: 16,
          itemStyle: {
            color: trade.type === 'buy' ? '#179B9080' : '#E3437680', // 使用半透明背景，参考app端
            borderColor: trade.type === 'buy' ? '#179B90' : '#E34376',
            borderWidth: 1,
          },
          label: {
            show: true,
            formatter: trade.type === 'buy' ? 'B' : 'S',
            position: 'inside',
            color: '#fff',
            fontSize: 11,
            fontWeight: 'bold',
          },
        };
      })
      .filter(Boolean); // 移除null值

    return { priceData: chartPriceData, tradePoints };
  }, [priceData, tradeData, t]);
}

export function InteractiveLineChart({
  priceData,
  tradeData,
  width = getScreenWidth() - 150,
  height = 300,
  precision = 2,
  interval,
}: InteractiveLineChartProps) {
  const t = useTranslations();

  // Use the custom hook to process chart data
  const { priceData: chartPriceData, tradePoints } = useChartData(
    priceData,
    tradeData,
  );

  // ECharts option configuration
  const option = useMemo(() => {
    if (chartPriceData.length === 0) {
      return {};
    }

    console.log('### tradePoints data:', tradePoints);
    console.log('### chartPriceData length:', chartPriceData.length);
    console.log('### sample chartPriceData:', chartPriceData.slice(0, 3));

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        show: true,
      },
      grid: {
        left: '3%',
        right: '10%',
        top: '10%',
        bottom: '15%',
        containLabel: true,
      },
      xAxis: {
        show: false,
        type: 'category',
        data: chartPriceData.map((data) => `${data[0]}`),
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          color: 'rgba(255, 255, 255, 0.4)',
          fontSize: 10,
          fontWeight: 'medium',
          padding: 15,
          interval: Math.max(Math.floor(chartPriceData.length / 3) - 1, 0),
          formatter: (value: string) => {
            return formatTime(
              Number(value),
              interval === '1d' || interval === '1w',
            );
          },
        },
      },
      yAxis: {
        type: 'value',
        position: 'right',
        scale: true,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: 'rgba(255, 255, 255, 0.4)',
          fontSize: 10,
          padding: 15,
          fontWeight: 'medium',
        },
        splitLine: { show: false },
      },
      series: [
        {
          name: 'Price',
          type: 'line',
          data: chartPriceData.map((data) => `${data[1]}`),
          smooth: false,
          symbol: 'none',
          lineStyle: {
            color: '#179B90',
            width: 1.5,
          },
        },
        {
          name: 'Trades',
          type: 'scatter',
          data: tradePoints,
          symbol: 'circle',
          symbolSize: 16,
          z: 100,
          emphasis: {
            scale: true,
            scaleSize: 20,
          },
          tooltip: {
            backgroundColor: 'rgba(32, 36, 50, 0.60)',
            borderColor: 'rgba(255, 255, 255, 0.10)',
            borderWidth: 0.5,
            borderRadius: 8,
            padding: 8,
            textStyle: {
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: 12,
              fontFamily:
                'Satoshi, -apple-system, BlinkMacSystemFont, sans-serif',
            },
            extraCssText: 'backdrop-filter: blur(8px);',
            formatter: (params: any) => {
              const data = params.data;
              const tradeType =
                data.type === 'buy'
                  ? t('trader.chart.long')
                  : t('trader.chart.short');
              // 从原始数据中找到对应的时间戳
              const priceIndex = data.value[0];
              const actualTimestamp = chartPriceData[priceIndex]
                ? chartPriceData[priceIndex][0]
                : Date.now();
              const timestamp = new Date(
                Number(actualTimestamp),
              ).toLocaleString();
              const typeColor = data.type === 'buy' ? '#179B90' : '#E34376';
              console.log('### data in chart tradePoints:', data);

              return `
                <div style="display: flex; flex-direction: column; gap: 8px; font-family: inherit;">
                  <div style="display: flex; align-items: center; gap: 4px;">
                    <div style="
                      background: ${data.type === 'buy' ? 'rgba(23, 155, 144, 0.15)' : 'rgba(227, 67, 118, 0.15)'};
                      color: ${typeColor};
                      padding: 2px 6px;
                      border-radius: 4px;
                      font-size: 12px;
                      font-weight: 600;
                    ">
                      ${tradeType}
                    </div>
                    <div style="
                      background: rgba(255, 255, 255, 0.08);
                      color: rgba(255, 255, 255, 0.8);
                      padding: 2px 6px;
                      border-radius: 4px;
                      font-size: 10px;
                      font-weight: 500;
                    ">
                      ${data.leverage}x
                    </div>
                  </div>
                  
                  <div style="display: flex; flex-direction: column; gap: 4px;">
                    <div style="display: flex; flex-direction: column; gap: 0;">
                      <span style="color: rgba(255, 255, 255, 0.6); font-size: 12px; font-weight: 500;">
                        ${t('trader.chart.amount')}
                      </span>
                      <span style="color: rgba(255, 255, 255, 0.9); font-size: 12px; font-weight: 600;">
                        ${formatCurrency(data.amount)}
                      </span>
                    </div>
                    
                    <div style="display: flex; flex-direction: column; gap: 0;">
                      <span style="color: rgba(255, 255, 255, 0.6); font-size: 12px; font-weight: 500;">
                        ${t('trader.chart.price')}
                      </span>
                      <span style="color: rgba(255, 255, 255, 0.9); font-size: 12px; font-weight: 600;">
                        ${formatPriceDisplay(data.value[1])}
                      </span>
                    </div>
                  </div>
                </div>
              `;
            },
          },
        },
      ],
      animation: true,
      animationDuration: 1000,
      animationEasing: 'cubicOut',
    };
  }, [chartPriceData, tradePoints, precision, t]);

  return (
    <Box width={width} height={height}>
      <DynamicReactECharts
        option={option}
        style={{ width: '100%', height: '100%' }}
        opts={{ renderer: 'canvas' }}
      />
    </Box>
  );
}
